import { AttachMoney, Chat, LocalOffer } from '@mui/icons-material';
import {
    Card,
    CardContent,
    Grid,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Skeleton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { OnlyFansPayout } from '../../../../../../hooks/useMetricsDailyStats';
import { DashboardProps } from '../SextforceMetricsAccountValueStats';
import ExportButtons from './ExportButtons';

// Helper function for currency formatting
const formatCurrency = (amount: number, withFraction: boolean = true): string => {
    return withFraction
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount)
        : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
};

// Get revenue breakdown data
const getRevenueBreakdownData = (
    theme: Theme,
    amountType: 'gross' | 'net',
    payouts?: OnlyFansPayout.PayoutsStats,
): {
    name: string;
    value: number;
    color: string;
}[] => {
    if (!payouts?.list?.total) return [];

    const { total } = payouts.list;

    if (!total) return [];
    if (amountType === 'gross') {
        return [
            { name: 'Messages', value: total.chat_messages?.total_gross || 0, color: theme.palette.primary.main },
            { name: 'Tips', value: total.tips?.total_gross || 0, color: theme.palette.secondary.main },
            { name: 'Posts', value: total.post?.total_gross || 0, color: theme.palette.success.main },
        ];
    }

    // Default to net
    return [
        { name: 'Messages', value: total.chat_messages?.total_net || 0, color: theme.palette.primary.main },
        { name: 'Tips', value: total.tips?.total_net || 0, color: theme.palette.secondary.main },
        { name: 'Posts', value: total.post?.total_net || 0, color: theme.palette.success.main },
    ];
};

const RevenueBreakdownCard: React.FC<DashboardProps> = ({
    subscriber,
    data,
    isLoading,
    latestEarnings,
    isLatestEarningsLoading,
    amountType,
    handleSetAmountType,
}) => {
    const theme = useTheme();

    if (isLatestEarningsLoading) {
        return (
            <Card elevation={3}>
                <CardContent>
                    <Skeleton variant="text" width={200} />
                    <Skeleton variant="rectangular" width="100%" height={300} />
                </CardContent>
            </Card>
        );
    }

    if (!latestEarnings) {
        return (
            <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1}>
                        <Typography variant="h6">Revenue Breakdown</Typography>
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                        No data available
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const earnings = latestEarnings;

    if (!earnings) return null;

    const revenueData = getRevenueBreakdownData(theme, amountType, earnings);
    const totalRevenue = revenueData.reduce((sum, item) => sum + item.value, 0);

    return (
        <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1}>
                    <Typography variant="h6">Revenue Breakdown</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <ToggleButtonGroup
                            value={amountType}
                            exclusive
                            color="secondary"
                            size="small"
                            onChange={(_event, newValue) => {
                                if (!newValue) {
                                    return;
                                }

                                handleSetAmountType(newValue);
                            }}
                            sx={{ marginTop: '4px', height: '39px' }}
                        >
                            <ToggleButton value="gross" fullWidth sx={{ minWidth: '60px' }}>
                                GROSS
                            </ToggleButton>
                            <ToggleButton value="net" fullWidth sx={{ minWidth: '60px' }}>
                                NET
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <ExportButtons
                            data={revenueData}
                            componentType="revenueBreakdown"
                            filename={`${subscriber.username}_Revenue_Breakdown`}
                        />
                    </Stack>
                </Stack>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={revenueData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                    nameKey="name"
                                    isAnimationActive={false}
                                >
                                    {revenueData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={value => [formatCurrency(value as number), undefined]} />
                            </PieChart>
                        </ResponsiveContainer>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <List>
                            {revenueData.map((item, index) => (
                                <React.Fragment key={index}>
                                    <ListItem>
                                        <ListItemIcon>
                                            {item.name === 'Messages' ? (
                                                <Chat sx={{ color: item.color }} />
                                            ) : item.name === 'Tips' ? (
                                                <LocalOffer sx={{ color: item.color }} />
                                            ) : (
                                                <AttachMoney sx={{ color: item.color }} />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.name}
                                            secondary={`${((item.value / totalRevenue) * 100).toFixed(1)}% of total revenue`}
                                        />
                                        <Typography variant="h6" fontWeight="bold">
                                            {formatCurrency(item.value)}
                                        </Typography>
                                    </ListItem>
                                    <LinearProgress
                                        variant="determinate"
                                        value={(item.value / totalRevenue) * 100}
                                        sx={{
                                            mb: 2,
                                            height: 10,
                                            borderRadius: 5,
                                            backgroundColor: `${item.color}40`,
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: item.color,
                                            },
                                        }}
                                    />
                                </React.Fragment>
                            ))}
                        </List>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default RevenueBreakdownCard;
